import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import type { CreateContractDto, Person } from '../types/contract.types';
import type { VehicleFormData } from '../types/vehicle.types';
import type { Warranty, WarrantyListItem } from '@/features/warranties/types/warranty.types';
import { validateCreateContract } from '../services/contractValidator';
import { useAuth } from '@/hooks/auth/useAuth';
import { normalizeNameSpaces } from '@/utils/nameHelpers';

/**
 * État du formulaire wizard de création de contrat
 */
export interface ContractFormState {
  // Step 1: Société + Utilisateur + Type véhicule + Garantie
  societyId?: string;
  userId?: string;
  userName?: { firstname: string; lastname: string };
  vehicleType?: number;
  warrantyUuid?: string;
  warrantyName?: string;

  // Step 2: Véhicule
  vehicle: VehicleFormData;

  // Step 3: Configuration
  subscriptionDate: string;
  extensionEndDate?: string;
  kmStart?: number;
  vehicleSellingPrice?: number;

  // Bénéficiaire
  subscriber: Person;
  beneficiary: Person;

  // Tarification
  paymentType: 1 | 2;
  paymentMethod?: 1 | 2 | 3;
  sellingPrice: number;
  monthlyPrice?: number;
  monthlyPriceWithFees?: number;
  quantity: number;
  includedOption: boolean;
  includedOptionLabel?: string;
  headTransferPrice: number;
  memberTransferPrice: number;
  commission: number;
  commissionType: 1 | 2;

  // Signature
  signatureType?: 1 | 2;

  // Vérification véhicule
  vehicleCheckReportUrl?: string;

  // Garantie personnalisée (survit au changement d'étape)
  customWarranty?: WarrantyListItem | null;
  customWarrantyRaw?: Warranty | null;
  customWarrantyPartIds?: number[];

  // Intermédiaire
  isIntermediary: boolean;

  // RGPD — refus de prospection (case affichée seulement si upsell activé)
  prospectionOptOut: boolean;

  // Autres
  externalId: string;
  buyingDate?: string;
  type: number;
}

const INITIAL_STATE: ContractFormState = {
  societyId: undefined,
  userId: undefined,
  vehicleType: undefined,
  warrantyUuid: undefined,
  subscriber: {
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
  },
  beneficiary: {
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
  },
  vehicle: {
    registration: '',
    serialNumber: '',
    brand: '',
    model: '',
  },
  paymentType: 1,
  sellingPrice: 0,
  quantity: 1,
  includedOption: false,
  headTransferPrice: 0,
  memberTransferPrice: 0,
  commission: 0,
  commissionType: 1,
  subscriptionDate: '',
  kmStart: undefined,
  vehicleSellingPrice: undefined,
  signatureType: 1,
  isIntermediary: false,
  prospectionOptOut: false,
  externalId: '',
  type: 0,
};

const SESSION_STORAGE_KEY = 'contract-wizard-session';
const SESSION_TTL_MS = 4 * 60 * 60 * 1000; // 4 heures

interface SessionSnapshot {
  formData: ContractFormState;
  currentStep: number;
  touchedSteps: number[];
  draftUuid: string | null;
  savedAt: number;
}

function getSessionSnapshot(): SessionSnapshot | null {
  try {
    const raw = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (!raw) return null;
    const snapshot: SessionSnapshot = JSON.parse(raw);
    if (Date.now() - snapshot.savedAt > SESSION_TTL_MS) {
      sessionStorage.removeItem(SESSION_STORAGE_KEY);
      return null;
    }
    // Ne pas restaurer si un draftId est dans l'URL (le loadFromDraft prendra le relais)
    if (new URLSearchParams(window.location.search).has('draftId')) return null;
    return snapshot;
  } catch {
    return null;
  }
}

/**
 * Hook de gestion du wizard de création de contrat
 */
export const useContractFormWizard = (initialSocietyId?: string) => {
  const { user } = useAuth();
  const isExternalUser = user?.type === 'EXTERNAL';

  const [sessionSnapshot] = useState(() => getSessionSnapshot());
  const isInitialized = useRef(false);

  const [formData, setFormData] = useState<ContractFormState>(() => {
    if (sessionSnapshot) return { ...INITIAL_STATE, ...sessionSnapshot.formData };
    return {
      ...INITIAL_STATE,
      societyId: initialSocietyId,
      userId: isExternalUser ? user?.id : undefined,
      userName: isExternalUser && user ? { firstname: user.firstname || '', lastname: user.lastname || '' } : undefined,
    };
  });
  const [currentStep, setCurrentStep] = useState(() => sessionSnapshot?.currentStep ?? 1);
  const [touchedSteps, setTouchedSteps] = useState<Set<number>>(() =>
    sessionSnapshot ? new Set(sessionSnapshot.touchedSteps) : new Set([1])
  );

  const [draftUuid, setDraftUuid] = useState<string | null>(() => sessionSnapshot?.draftUuid ?? null);

  // Persister dans sessionStorage à chaque changement (après l'init)
  useEffect(() => {
    if (!isInitialized.current) {
      isInitialized.current = true;
      return;
    }
    try {
      const snapshot: SessionSnapshot = {
        formData,
        currentStep,
        touchedSteps: [...touchedSteps],
        draftUuid,
        savedAt: Date.now(),
      };
      sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(snapshot));
    } catch { /* sessionStorage plein ou indisponible */ }
  }, [formData, currentStep, touchedSteps, draftUuid]);

  const totalSteps = 4; // Max steps — container controls actual flow

  const updateFormData = useCallback((updates: Partial<ContractFormState>) => {
    setFormData((prev) => ({ ...prev, ...updates }));
  }, []);

  const goToNextStep = useCallback(() => {
    if (currentStep < totalSteps) {
      const nextStep = currentStep + 1;
      setCurrentStep(nextStep);
      setTouchedSteps((prev) => new Set([...prev, nextStep]));
    }
  }, [currentStep, totalSteps]);

  const goToPreviousStep = useCallback(() => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  }, [currentStep]);

  const goToStep = useCallback((step: number) => {
    if (step >= 1 && step <= totalSteps) {
      setCurrentStep(step);
      setTouchedSteps((prev) => new Set([...prev, step]));
    }
  }, [totalSteps]);

  const validationErrors = useMemo(
    () => validateCreateContract(formData as CreateContractDto),
    [formData],
  );

  const isFormValid = useMemo(
    () => validationErrors.length === 0,
    [validationErrors],
  );

  const toCreateDto = useCallback((societyOnlinePayment?: boolean): CreateContractDto => {
    // Nettoyage des espaces sur prénom/nom avant envoi à l'API (YouSign rejette
    // les espaces en début/fin ou multiples sur info.first_name/last_name).
    const cleanPerson = (p: Person): Person => ({
      ...p,
      firstName: normalizeNameSpaces(p?.firstName),
      lastName: normalizeNameSpaces(p?.lastName),
    });
    return {
      societyId: formData.societyId,
      userId: formData.userId,
      warrantyUuid: formData.warrantyUuid!,
      externalId: formData.externalId,
      subscriptionDate: formData.subscriptionDate,
      extensionEndDate: formData.extensionEndDate,
      buyingDate: formData.buyingDate,
      subscriber: cleanPerson(formData.subscriber),
      beneficiary: cleanPerson(formData.beneficiary),
      vehicle: formData.vehicle,

      kmStart: formData.kmStart,
      vehicleSellingPrice: formData.vehicleSellingPrice,

      paymentType: formData.paymentType,
      isOnlinePayment: !formData.includedOption && formData.sellingPrice > 0 && !!societyOnlinePayment && formData.paymentType === 1 && formData.paymentMethod !== 3,
      paymentMethod: societyOnlinePayment && formData.paymentType === 1 ? formData.paymentMethod : undefined,
      sellingPrice: formData.sellingPrice,
      monthlyPrice: formData.monthlyPrice,
      quantity: formData.quantity,
      includedOption: formData.includedOption,
      includedOptionLabel: formData.includedOption ? formData.includedOptionLabel : undefined,
      commission: formData.commission > 0 ? formData.commission : undefined,
      commissionType: formData.commission > 0 ? formData.commissionType : undefined,

      signatureType: formData.signatureType,
      vehicleCheckReportUrl: formData.vehicleCheckReportUrl,
      isIntermediary: formData.isIntermediary || undefined,
      draftUuid: draftUuid || undefined,
      type: formData.type,
      prospectionOptOut: formData.prospectionOptOut || undefined,
    };
  }, [formData, draftUuid]);

  const copySubscriberToBeneficiary = useCallback(() => {
    setFormData((prev) => ({
      ...prev,
      beneficiary: { ...prev.subscriber },
    }));
  }, []);

  const loadFromDraft = useCallback((draft: { uuid: string; currentStep: number; formData: Record<string, any> }) => {
    const restored: ContractFormState = {
      ...INITIAL_STATE,
      ...draft.formData,
    };
    setFormData(restored);
    setCurrentStep(draft.currentStep);
    const steps = new Set<number>();
    for (let i = 1; i <= draft.currentStep; i++) {
      steps.add(i);
    }
    setTouchedSteps(steps);
    setDraftUuid(draft.uuid);
  }, []);

  const reset = useCallback(() => {
    setFormData(INITIAL_STATE);
    setCurrentStep(1);
    setTouchedSteps(new Set([1]));
    setDraftUuid(null);
    sessionStorage.removeItem(SESSION_STORAGE_KEY);
  }, []);

  return {
    formData,
    currentStep,
    totalSteps,
    touchedSteps,
    validationErrors,
    isFormValid,
    draftUuid,
    updateFormData,
    goToNextStep,
    goToPreviousStep,
    goToStep,
    toCreateDto,
    copySubscriberToBeneficiary,
    loadFromDraft,
    setDraftUuid,
    reset,
  };
};
